import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ROUTING_BASENAME } from "../../config";
import { useAppSelector } from "../hooks";
import { AppDispatch, RootState } from "../store";
import { createBrowserRouter, Location, Route, RouteMatch, RouterState } from "./router";

export interface RoutingState {
    initialized: boolean;
    location: Location | null;
    matches: RouteMatch[];
    errors: ReturnType<typeof convertErrors> | null;
}

export interface RoutingChangePayload {
    location: Location;
    matches: RouteMatch[];
    errors: ReturnType<typeof convertErrors> | null;
}

const routes: Route[] = [
    {
        id: "start",
        path: "/",
    },
    {
        id: "invite",
        path: "/invite",
    },
    {
        id: "redirect",
        path: "/redirect/:next",
    },
    {
        id: "error",
        path: "/error",
    },
    {
        id: "logout",
        path: "/logout",
    },
    {
        id: "application-login",
        path: "/app/:application",
    },
    {
        id: "forgot-password",
        path: "service/forgot-password",
    },
    {
        id: "reset-password",
        path: "service/reset-password",
    },
];

const router = createBrowserRouter({ routes, basename: ROUTING_BASENAME });

const initialState: RoutingState = {
    initialized: false,
    location: null,
    matches: [],
    errors: null,
};

const routingSlice = createSlice({
    name: "routing",
    initialState,
    reducers: {
        initialize(state) {
            state.initialized = true;
        },
        change(state, { payload }: PayloadAction<RoutingChangePayload>) {
            state.location = payload.location;
            state.matches = payload.matches;
            state.errors = payload.errors;
        },
    },
});

/**
 * Initializes routing and hooks it up with the given Redux app dispatch to keep
 * track of routing state changes.
 */
export function initializeRouting(dispatch: AppDispatch) {
    function syncRoutingState(state: RouterState) {
        dispatch(
            routingSlice.actions.change({
                matches: state.matches,
                location: state.location,
                errors: state.errors ? convertErrors(state.errors) : null,
            })
        );
    }

    router.subscribe(syncRoutingState);
    router.initialize();

    // first explicit sync on initial page load
    syncRoutingState(router.getState());

    dispatch(routingSlice.actions.initialize());
}

// error conversion to that make sure only serializable stuff ends up in the
// Redux state; we probably want to clean this up to make more assertions about
// the output (i.e. have nice error types instead of just "object" etc)

function convertErrors(errors: object) {
    return Object.fromEntries(Object.entries(errors).map(([id, value]) => [id, convertError(value)]));
}

function convertError(error: object) {
    return Object.fromEntries(Object.entries(error).map(([name, value]) => [name, convertErrorPropertyValue(value)]));
}

function convertErrorPropertyValue(value: any) {
    switch (typeof value) {
        case "string":
        case "boolean":
        case "number":
        case "undefined":
            return value;
        default:
            return value === null ? null : (value.toString() as string);
    }
}

// selectors

export type RoutingSummary = InitializedRoutingSummary | UninitializedRoutingSummary;

export interface InitializedRoutingSummary {
    ready: true;
    match: RouteMatch;
    matches: RouteMatch[];
    error: ReturnType<typeof convertError> | null;
    location: Location;
}

export interface UninitializedRoutingSummary {
    ready: false;
    match: null;
    matches: RouteMatch[];
    error: null;
    location: null;
}

export function selectRouting(state: RootState): RoutingSummary {
    const routingState = state[routingSlice.name];
    const { initialized, location, matches } = routingState;

    if (!initialized || location === null) {
        return {
            ready: false,
            match: null,
            matches: [],
            error: null,
            location: null,
        };
    }

    const match = matches.at(-1) as RouteMatch; // the lib always returns at least one item
    const error = routingState.errors ? routingState.errors[match.route.id] : null;

    return {
        ready: true,
        match,
        matches,
        error,
        location,
    };
}

function selectSearchParams(state: RootState): URLSearchParams {
    return new URLSearchParams(state[routingSlice.name].location?.search);
}

// hooks

/**
 * Returns an overall collection of current routing state.
 */
export function useRouting() {
    return useAppSelector(selectRouting);
}

/**
 * Returns the current URL query/search parameters. Returns an empty parameter
 * container if the URL does not contain any.
 */
export function useRoutingSearchParams() {
    return useAppSelector(selectSearchParams);
}

// other helpers

export const { navigate } = router;
export const { createHref } = router;

export default routingSlice;
